import { Helmet } from 'react-helmet-async';
import { siteURL } from '~/utils/constants/env';

export type SEOType = 'home' | 'features' | 'pricing' | 'about' | 'privacyPolicy' | 'termsOfService' | 'cookiePolicy';

type SEOMeta = {
  title: string;
  description: string;
  path: string;
  noIndex?: boolean;
};

const SEO_META: Record<SEOType, SEOMeta> = {
  home: {
    title: 'Grow into the Best Version of Yourself',
    description:
      'Lumi is your AI-powered life companion — combining AI coaching, goal tracking, habit building, and journaling into one deeply personal experience.',
    path: '/',
  },
  features: {
    title: 'Features',
    description:
      'Explore all Lumi features: AI companion chat, smart goal tracking, daily journaling, and habit building — all in one beautiful app.',
    path: '/features',
  },
  pricing: {
    title: 'Pricing',
    description:
      "Simple, transparent pricing. Start for free and upgrade when you're ready to unlock the full power of Lumi.",
    path: '/pricing',
  },
  about: {
    title: 'About Us',
    description:
      'Lumi was built for people who want to grow. Learn about our story, our values, and our mission to be the most personal AI life companion.',
    path: '/about',
  },
  privacyPolicy: {
    title: 'Privacy Policy',
    description: 'Learn how Lumi collects, uses, and protects your personal data.',
    path: '/privacy-policy',
    noIndex: true,
  },
  termsOfService: {
    title: 'Terms of Service',
    description: 'Read the terms and conditions governing your use of Lumi.',
    path: '/terms-of-service',
    noIndex: true,
  },
  cookiePolicy: {
    title: 'Cookie Policy',
    description: 'Learn how Lumi uses cookies and similar tracking technologies.',
    path: '/cookie-policy',
    noIndex: true,
  },
};

const DEFAULT_OG_IMAGE = `${siteURL}/og-image.png`;

type Props = {
  type: SEOType;
  ogImage?: string;
};

const SEO: React.FC<Props> = ({ type, ogImage = DEFAULT_OG_IMAGE }) => {
  const { title, description, path, noIndex } = SEO_META[type];
  const fullTitle = `${title} | Lumi`;
  const canonicalURL = `${siteURL}${path}`;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name='description' content={description} />
      <link rel='canonical' href={canonicalURL} />
      {noIndex && <meta name='robots' content='noindex, nofollow' />}

      {/* Open Graph */}
      <meta property='og:type' content='website' />
      <meta property='og:url' content={canonicalURL} />
      <meta property='og:title' content={fullTitle} />
      <meta property='og:description' content={description} />
      <meta property='og:image' content={ogImage} />
      <meta property='og:site_name' content='Lumi' />

      {/* Twitter Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:url' content={canonicalURL} />
      <meta name='twitter:title' content={fullTitle} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={ogImage} />
    </Helmet>
  );
};

export default SEO;
